/**
 * Machine Modal Component
 *
 * Simplified form for creating or editing machines.
 * Only captures: Machine ID, Machine Name, and Active status.
 */

import React, { useState, useEffect } from 'react';
import { MachineCreate, MachineUpdate, MachineWithShifts } from '../types/machine';

interface MachineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (machine: MachineCreate | MachineUpdate) => Promise<void>;
  machine?: MachineWithShifts | null;
  mode: 'create' | 'edit';
  selectedPlantId: number;
  selectedDepartmentId: number;
}

const MachineModal: React.FC<MachineModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  machine,
  mode,
  selectedPlantId,
  selectedDepartmentId,
}) => {
  const [machineId, setMachineId] = useState('');
  const [machineName, setMachineName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when editing or creating
  useEffect(() => {
    if (mode === 'edit' && machine) {
      setMachineId(machine.machine_id);
      setMachineName(machine.machine_name);
      setIsActive(machine.is_active);
    } else {
      // Reset form when creating new machine
      setMachineId('');
      setMachineName('');
      setIsActive(true);
    }
    setError(null);
  }, [mode, machine, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!machineId.trim()) {
        throw new Error('Machine ID is required');
      }
      if (!machineName.trim()) {
        throw new Error('Machine Name is required');
      }
      if (!selectedPlantId || selectedPlantId === 0) {
        throw new Error('Please select a plant first');
      }
      if (!selectedDepartmentId || selectedDepartmentId === 0) {
        throw new Error('Please select a department first');
      }

      // Create machine data
      const machineData: MachineCreate | MachineUpdate = {
        plant_id: selectedPlantId,
        department_id: selectedDepartmentId,
        machine_id: machineId.trim(),
        machine_name: machineName.trim(),
        is_active: isActive,
      };

      await onSubmit(machineData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save machine');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="rounded-lg shadow-xl p-6 w-full max-w-md"
        style={{ background: 'var(--bg-secondary)' }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {mode === 'create' ? 'Add New Machine' : 'Edit Machine'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={loading}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="machine_id"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Machine ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="machine_id"
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              required
              disabled={mode === 'edit'}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
              }}
              placeholder="e.g., CNC-001"
            />
            {mode === 'edit' && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                Machine ID cannot be changed
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="machine_name"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Machine Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="machine_name"
              value={machineName}
              onChange={(e) => setMachineName(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
              }}
              placeholder="e.g., CNC Machine 1"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="is_active"
              className="ml-2 block text-sm"
              style={{ color: 'var(--text-primary)' }}
            >
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50"
              style={{
                background: 'var(--btn-bg)',
                color: 'var(--btn-text)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Machine' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MachineModal;
